/**
 * DOCUMENTATION:
 * Component: GCPBigData
 * Purpose: Deep dive into BigQuery, Dataflow, Pub/Sub, and Dataproc.
 * * * Note for Junior Developers:
 * - Decoupling: Pub/Sub lets systems talk without being "stuck" together.
 * - Serverless Analysis: BigQuery can scan a petabyte in seconds because
 * it separates storage from processing.
 * - Windowing: Dataflow can group data by time (e.g., "Total sales every 5 minutes").
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from "@mui/material";

/* Icons */
import AnalyticsIcon from '@mui/icons-material/Analytics';
import StreamIcon from '@mui/icons-material/Stream';
import InsightsIcon from '@mui/icons-material/Insights';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import HubIcon from '@mui/icons-material/Hub';
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

const PipelineStep = styled(Box)(({ theme, color }) => ({
    padding: theme.spacing(3),
    textAlign: "center",
    borderRadius: "12px",
    border: `2px solid ${color}`,
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    flex: 1,
    transition: "transform 0.3s ease",
    "&:hover": {
        transform: "translateY(-5px)",
        backgroundColor: "rgba(255, 255, 255, 0.06)",
    }
}));

export default function GCPBigData() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    BIG DATA & PIPELINES
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Architecting for the Petabyte Era</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. The Real-Time Analytics Pipeline */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <StreamIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>The "Golden Path" Pipeline</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.8 }}>
                            Modern architects design pipelines that are <strong>asynchronous</strong> and <strong>serverless</strong>.
                            The pipeline below handles millions of events per second from mobile apps or IoT devices.
                        </Typography>



                        <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={3}
                            sx={{ mt: 4, alignItems: "stretch" }}
                        >
                            <PipelineStep color="#4285F4">
                                <HubIcon sx={{ color: "#4285F4", fontSize: 40, mb: 1 }} />
                                <Typography variant="h6" fontWeight={800}>INGEST</Typography>
                                <Typography variant="subtitle2" sx={{ color: "#4285F4" }}>Pub/Sub</Typography>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                                    Global "Shock Absorber." Ingests streaming data from any source and holds it until processing is ready.
                                </Typography>
                            </PipelineStep>
                            <PipelineStep color="#34A853">
                                <FilterAltIcon sx={{ color: "#34A853", fontSize: 40, mb: 1 }} />
                                <Typography variant="h6" fontWeight={800}>PROCESS</Typography>
                                <Typography variant="subtitle2" sx={{ color: "#34A853" }}>Dataflow</Typography>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                                    Serverless Apache Beam. Deduplicates data, transforms formats, and handles "late" messages.
                                </Typography>
                            </PipelineStep>
                            <PipelineStep color="#FBBC04">
                                <AnalyticsIcon sx={{ color: "#FBBC04", fontSize: 40, mb: 1 }} />
                                <Typography variant="h6" fontWeight={800}>STORE</Typography>
                                <Typography variant="subtitle2" sx={{ color: "#FBBC04" }}>BigQuery</Typography>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                                    The Data Warehouse. Scales compute and storage independently. Run SQL on petabytes.
                                </Typography>
                            </PipelineStep>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 2. BigQuery Cost & Performance */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <InsightsIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>Optimizing BigQuery</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.8 }}>
                            BigQuery pricing is based on **Bytes Scanned**. For the PCA exam, you must know how to keep
                            costs low and performance high using partitioning and clustering.
                        </Typography>



                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: "rgba(66, 133, 244, 0.1)", border: "1px solid #4285F4", color: "white" }}>
                                    <Typography variant="subtitle2" color="#4285F4">Partitioning</Typography>
                                    <Typography variant="caption">Divides tables into segments based on a date or timestamp. Prevents BigQuery from scanning the whole table if you only need today's data.</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: "rgba(52, 168, 83, 0.1)", border: "1px solid #34A853", color: "white" }}>
                                    <Typography variant="subtitle2" color="#34A853">Clustering</Typography>
                                    <Typography variant="caption">Sorts data based on the values in one or more columns (e.g., UserID). Makes 'WHERE' filters and 'GROUP BY' much faster.</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. Hadoop/Spark Migration */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <AutoFixHighIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Migration: Dataproc</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                            What if a client already has a large **Apache Spark** or **Hadoop** cluster on-premises?
                        </Typography>
                        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)", borderRadius: "8px", border: "1px dashed #FBBC04" }}>
                            <Typography variant="h6" color="#FBBC04" textAlign="center">Dataproc</Typography>
                            <Typography variant="body2" sx={{ display: 'block', mt: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                A "Lift and Shift" for Big Data. It manages open-source Hadoop/Spark clusters so you can
                                move your existing jobs without rewriting them in Dataflow.
                            </Typography>
                        </Box>


                    </SectionCard>
                </Grid>

                {/* PCA Exam Scenario */}
                <Grid item xs={12}>
                    <Alert severity="success" sx={{ bgcolor: "rgba(52, 168, 83, 0.1)", color: "white", border: "1px solid #34A853" }}>
                        <strong>PCA ARCHITECT SCENARIO:</strong> "Your company needs to analyze log data coming from 50,000 servers. They want to visualize this data in real-time to detect security threats. Costs must be predictable."
                        <br/><br/>
                        <strong>Correct Strategy:</strong> Ingest via <strong>Pub/Sub</strong>, process/clean via <strong>Dataflow</strong>, store in <strong>BigQuery</strong> using <strong>Partitioned Tables</strong>, and use <strong>BigQuery Slot Reservations</strong> (Flat-rate pricing) for predictable monthly billing.
                    </Alert>
                </Grid>

                {/* Navigation */}
                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
